import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  AlertTriangle, 
  User, 
  Clock, 
  MapPin, 
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Camera,
  FileText,
  Shield,
  Zap
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface EmergencyReport {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'investigating' | 'resolved';
  guard_name: string;
  guard_id: string;
  location: string;
  image_url?: string;
  team_id: string;
  created_at: string;
  updated_at: string;
  notes: any[];
  resolved_by?: string;
  resolved_at?: string;
}

interface EmergencyReportsListProps {
  teamId: string;
  dateFilter?: { start: Date; end: Date };
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
    case 'investigating': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const EmergencyReportsList: React.FC<EmergencyReportsListProps> = ({ 
  teamId, 
  dateFilter 
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(new Set());

  // Get guard names for all reports
  const { data: guardNamesMap } = useQuery({
    queryKey: ['guardNamesForEmergencyReports', teamId],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .eq('role', 'guard');
      
      const nameMap: Record<string, string> = {};
      data?.forEach((profile) => {
        nameMap[profile.id] = profile.full_name || 
          `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
          'Unknown Guard';
      });
      
      return nameMap;
    }
  });

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey: ['emergency-reports-list', teamId, dateFilter],
    queryFn: async () => {
      let query = supabase
        .from('emergency_reports')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });

      if (dateFilter) {
        query = query
          .gte('created_at', dateFilter.start.toISOString())
          .lte('created_at', dateFilter.end.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EmergencyReport[];
    }
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const toggleReportExpansion = (reportId: string) => {
    const newExpanded = new Set(expandedReports);
    if (newExpanded.has(reportId)) {
      newExpanded.delete(reportId);
    } else {
      newExpanded.add(reportId);
    }
    setExpandedReports(newExpanded);
  };

  // Auto-refresh every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading emergency reports...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <h3 className="text-lg font-semibold">
            Emergency Reports ({reports?.length || 0})
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Reports List */}
      {!reports || reports.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Emergency Reports Found</h3>
            <p className="text-muted-foreground">
              No emergency reports found for this team.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const guardName = report.guard_id ? guardNamesMap?.[report.guard_id] : null;
            const isExpanded = expandedReports.has(report.id);
            
            return (
              <Card key={report.id} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleReportExpansion(report.id)}>
                  <CollapsibleTrigger asChild>
                    <div className="w-full cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-medium truncate">
                                {report.title}
                              </CardTitle>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span>{format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}</span>
                                {guardName && <span>Guard: {guardName}</span>}
                                {report.location && <span>Location: {report.location}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <Badge className={`${getSeverityColor(report.severity)} text-xs`}>
                              {report.severity}
                            </Badge>
                            <Badge className={`${getStatusColor(report.status)} text-xs`}>
                              {report.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        {/* Guard */}
                        {guardName && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-500" />
                            <span className="text-muted-foreground">Guard:</span>
                            <span className="font-medium truncate">{guardName}</span>
                          </div>
                        )}
                        
                        {/* Location */}
                        {report.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Location:</span>
                            <span className="font-medium truncate">{report.location}</span>
                          </div>
                        )}
                        
                        {/* Date */}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Created:</span>
                          <span className="font-medium">
                            {format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      {report.description && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-600" />
                            Description
                          </h4>
                          <p className="text-sm text-muted-foreground">{report.description}</p>
                        </div>
                      )}

                      {/* Photo Display */}
                      {report.image_url && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Camera className="h-4 w-4 text-blue-600" />
                            Photo Evidence
                          </h4>
                          <div className="relative">
                            <img 
                              src={report.image_url} 
                              alt="Emergency report evidence" 
                              className="w-full max-w-md rounded-lg border shadow-sm"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {report.notes && report.notes.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3">Notes ({report.notes.length})</h4>
                          <div className="space-y-2">
                            {report.notes.map((note: any, index: number) => (
                              <div key={index} className="p-3 bg-muted rounded-lg">
                                <p className="text-sm">{note.content || note}</p>
                                {note.created_at && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
